import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import restaurantService, { formatSpaceLabel, MenuItem, Restaurant, CustomerDisplaySettings } from '../../services/restaurantService';
import cartService, { apiErrorMessage, CartOptions } from '../../services/cartService';
import { promptCancelOpenPayment } from '../../utils/paymentCheckout';
import type { CartActionResult, MenuCardAddToCartFlags } from '../../hooks/useMenuItemCartControls';
import {
  getInitialSelections,
  MenuOrderSelections,
  validateOrderSelections,
} from '../common/MenuOrderOptions/MenuOrderOptions';
import MenuItemOptionsSheet from '../common/MenuItemOptionsSheet/MenuItemOptionsSheet';
import {
  getDisplayMrp,
  getDisplayPrice,
  getEffectiveDisplayPrice,
  getStrikeThroughPrice,
  getMenuOptionsMeta,
  roundDisplayPrice,
} from '../../utils/menuPricing';
import wishlistService from '../../services/wishlistService';
import { DEFAULT_IMAGES, getMenuItemImageUrl, getRestaurantImageUrl } from '../../constants/images';
import { useAppSelector } from '../../store/hooks';
import Header from '../Header/Header';
import Footer from '../Footer/Footer';
import PageContainer from '../PageContainer/PageContainer';
import Breadcrumb from '../Breadcrumb/Breadcrumb';
import LoginModal from '../LoginModal/LoginModal';
import { hasPendingSocialAuthError } from '../../utils/socialAuth';
import MessageDialog from '../common/MessageDialog/MessageDialog';
import { isWishlistLoginRequired } from '../../utils/wishlistAuth';
import {
  IconBookmark,
  IconBookmarkFilled,
  IconCart,
  IconCartFab,
  IconFilterSliders,
  IconFilterSlidersFab,
  stripTagEmoji,
} from './MenuListingIcons';
import Toast from '../Toast/Toast';
import { CART_UPDATED_EVENT } from '../../utils/cartEvents';
import { useRealtime } from '../../contexts/RealtimeContext';
import { MenuPageSkeleton } from './MenuSkeleton';
import { useEmailSignInPrompt } from '../../hooks/useEmailSignInPrompt';
import { useGuestOrderType } from '../../hooks/useGuestOrderType';
import { withGuestOrderTypeQuery } from '../../utils/guestOrderTypeStorage';
import { saveGuestOrderType } from '../../utils/guestOrderTypeStorage';
import { useGuestCanOrder } from '../../hooks/useGuestCanOrder';
import GuestOrderingBanner from '../common/GuestOrderingBanner/GuestOrderingBanner';
import { guestOrderingMessage, GuestOrderingStatus, isAcceptingGuestOrders } from '../../utils/guestOrdering';
import MenuViewSwitch from '../MenuBrowse/MenuViewSwitch';
import MenuItemCard from '../Menu/MenuItemCard';
import { getRestaurantBreadcrumbLabel, restaurantHomePath } from '../../utils/restaurantBreadcrumb';
import OffersDrawer from '../common/OffersDrawer/OffersDrawer';
import { formatCouponBadge, formatCouponText } from '../../utils/offerFormatting';
import { useSsrPreload, matchSsrPreload } from '../../ssr/preload';
import SeoHead from '../Seo/SeoHead';
import {
  buildMenuPageTitle,
  buildMenuPageDescription,
  shouldIndexMenuPage,
} from '../../utils/seoMetadata';
import MobileMenuFilterOverlay from './MobileMenuFilterOverlay';
import './RestaurantMenu.css';

interface Category {
  id: number;
  type: string;
}

interface Cuisine {
  id: number;
  name: string;
}

interface MenuFilter {
  itemType: string;
  categories: number[];
  cuisines: number[];
  tags: string[];
  priceFilterEnabled: boolean;
  minPrice: number;
  maxPrice: number;
}

const RestaurantMenu: React.FC = () => {
  const { appName } = useParams<{ appName: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, authReady } = useAppSelector((state) => state.user);
  const guestOrderType = useGuestOrderType(appName);
  const { canOrder, loaded: canOrderLoaded } = useGuestCanOrder(appName);
  const preload = useSsrPreload();
  const menuPreload = matchSsrPreload(preload, 'menu', appName) ? preload : null;
  const [restaurant, setRestaurant] = useState<Restaurant | null>(menuPreload?.restaurant ?? null);
  const [guestOrdering, setGuestOrdering] = useState<GuestOrderingStatus | null>(menuPreload?.guestOrdering ?? null);
  const [displaySettings, setDisplaySettings] = useState<CustomerDisplaySettings | null>(menuPreload?.displaySettings ?? null);
  const [menuItems, setMenuItems] = useState<MenuItem[]>(menuPreload?.menuItems ?? []);
  const [allMenuItems, setAllMenuItems] = useState<MenuItem[]>(menuPreload?.menuItems ?? []);
  const [categories, setCategories] = useState<Category[]>(
    (menuPreload?.categories || []).map((c) => ({ id: c.id, type: c.type || c.name || '' }))
  );
  const [cuisines, setCuisines] = useState<Cuisine[]>(
    (menuPreload?.cuisines || []).map((c) => ({ id: c.id, name: c.name || '' }))
  );
  const [tags, setTags] = useState<string[]>([]);
  const [loading, setLoading] = useState(!menuPreload?.menuItems);
  const [error, setError] = useState<string | null>(null);
  const [cartCount, setCartCount] = useState(0);
  const [showWishlist, setShowWishlist] = useState(false);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(() => hasPendingSocialAuthError());
  const [loginModalTitle, setLoginModalTitle] = useState('Login Required');
  const [loginModalMessage, setLoginModalMessage] = useState('Please login to continue.');
  const [messageDialog, setMessageDialog] = useState<{
    title: string;
    message: string;
    variant?: 'info' | 'error';
  } | null>(null);
  const loginSuccessCallbackRef = useRef<(() => void) | null>(null);
  const [tableRequestStatus, setTableRequestStatus] = useState<{
    requestId?: number;
    allotted?: boolean;
    tableNumber?: number;
    spaceType?: 'TABLE' | 'ROOM';
    displayLabel?: string;
    message?: string;
  } | null>(null);
  const [tokenProcessed, setTokenProcessed] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [activePromotions, setActivePromotions] = useState<Array<{ id: number; name: string; description?: string; discountPreview?: string }>>([]);
  const [publicCoupons, setPublicCoupons] = useState<
    Array<{
      id: number;
      couponCode?: string;
      couponType?: string;
      discount?: number;
      minAmount?: number;
      description?: string;
    }>
  >([]);
  const [offersDrawerOpen, setOffersDrawerOpen] = useState(false);
  const isProcessingRef = useRef(false);
  const retryCountRef = useRef(0);
  const menuHydratedRef = useRef(!!menuPreload);
  const menuDataReadyRef = useRef(!!menuPreload);
  const loadRequestRef = useRef(0);
  const allMenuItemsRef = useRef<MenuItem[]>([]);
  const prevAppNameRef = useRef<string | undefined>(undefined);
  const tokenCallTimerRef = useRef<NodeJS.Timeout | null>(null);
  const toolbarRef = useRef<HTMLDivElement | null>(null);
  const [showScrollFilterFab, setShowScrollFilterFab] = useState(false);
  const { subscribeTableRequestStatus } = useRealtime();

  useEffect(() => {
    if (!tableRequestStatus || tableRequestStatus.allotted) {
      return undefined;
    }
    return subscribeTableRequestStatus((payload) => {
      if (payload.approved) {
        setTableRequestStatus((prev) => ({
          ...prev,
          allotted: true,
          message: 'Your table request was approved',
        }));
      } else if (payload.approved === false) {
        setTableRequestStatus((prev) => ({
          ...prev,
          message: 'Your table request was declined',
        }));
      }
    });
  }, [tableRequestStatus, subscribeTableRequestStatus]);

  // Filter states
  const [selectedCategories, setSelectedCategories] = useState<number[]>([]);
  const [selectedCuisines, setSelectedCuisines] = useState<number[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [itemType, setItemType] = useState<string>('all'); // 'all', 'veg', 'nonveg'
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 1000]);
  const [sortBy, setSortBy] = useState<string>('default'); // 'default', 'price-asc', 'price-desc', 'rating', 'name-asc', 'name-desc'

  useEffect(() => {
    document.body.classList.add('menu-page-active');
    return () => document.body.classList.remove('menu-page-active');
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.scrollTo(0, 0);
    }

    if (!appName) return;

    const isNewRestaurant = prevAppNameRef.current !== appName;
    if (isNewRestaurant) {
      prevAppNameRef.current = appName;
      const hasPreload = !!menuPreload && menuPreload.appName === appName;
      if (!hasPreload) {
        menuDataReadyRef.current = false;
        menuHydratedRef.current = false;
      }
    } else if (menuDataReadyRef.current) {
      loadCartCount();
      return;
    }

    if (menuPreload && menuPreload.appName === appName && menuDataReadyRef.current) {
      loadCartCount();
      return;
    }

    const requestId = ++loadRequestRef.current;
    loadMenu(requestId);
    loadCartCount();
  }, [appName]);

  useEffect(() => {
    if (!appName) return;
    restaurantService
      .getRestaurant(appName)
      .then((res) => {
        if (res.success && res.restaurant) {
          setRestaurant((prev) => prev ?? res.restaurant);
          setGuestOrdering(res.guestOrdering ?? null);
        }
      })
      .catch(() => {});
  }, [appName]);

  useEffect(() => {
    if (!appName) return undefined;

    const handler = (event: Event) => {
      const detail = (event as CustomEvent<{ appName: string; count?: number }>).detail;
      if (detail?.appName !== appName) {
        return;
      }
      if (typeof detail.count === 'number') {
        setCartCount(detail.count);
        return;
      }
      loadCartCount();
    };

    window.addEventListener(CART_UPDATED_EVENT, handler);
    return () => window.removeEventListener(CART_UPDATED_EVENT, handler);
  }, [appName]);
  
  // QR token: wait for auth hydration, then login first or process table request
  useEffect(() => {
    if (!appName || !authReady) return;

    const token = searchParams.get('token');
    if (!token) return;

    if (tokenProcessed || isProcessingRef.current) {
      return;
    }

    if (!isAuthenticated || !user) {
      setLoginModalTitle('Login Required');
      setLoginModalMessage('Sign in to request a table.');
      loginSuccessCallbackRef.current = null;
      setShowLoginModal(true);
      return;
    }

    if (tokenCallTimerRef.current) {
      clearTimeout(tokenCallTimerRef.current);
      tokenCallTimerRef.current = null;
    }

    isProcessingRef.current = false;
    retryCountRef.current = 0;
    handleQRToken(token, 0);

    return () => {
      if (tokenCallTimerRef.current) {
        clearTimeout(tokenCallTimerRef.current);
        tokenCallTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appName, searchParams, authReady, isAuthenticated, user, tokenProcessed]);

  // Re-check token when user becomes authenticated
  useEffect(() => {
    if (isAuthenticated && user && appName) {
      const token = searchParams.get('token');
      if (token && !tokenProcessed && !isProcessingRef.current) {
        // Clear any existing timer
        if (tokenCallTimerRef.current) {
          clearTimeout(tokenCallTimerRef.current);
        }
        // Reset refs when retrying after login
        isProcessingRef.current = false;
        retryCountRef.current = 0;
        // Longer delay to ensure auth cookie is set and Redux store is fully updated
        tokenCallTimerRef.current = setTimeout(() => {
          if (!isProcessingRef.current) {
            handleQRToken(token, 0);
          }
          tokenCallTimerRef.current = null;
        }, 1500);
        return () => {
          if (tokenCallTimerRef.current) {
            clearTimeout(tokenCallTimerRef.current);
            tokenCallTimerRef.current = null;
          }
        };
      }
    }
  }, [isAuthenticated, user, appName, tokenProcessed]);

  const handleQRToken = async (token: string, retryCount: number = 0) => {
    if (!appName || tokenProcessed || isProcessingRef.current) {
      return;
    }
    saveGuestOrderType(appName, 1);
    
    // Check total retry count (including this attempt)
    const totalAttempts = retryCount + 1;
    if (totalAttempts > 3) {
      setToastMessage('Unable to process table request after multiple attempts. Please contact the restaurant for assistance.');
      setTokenProcessed(true);
      return;
    }
    
    // Mark as processing to prevent duplicate calls
    isProcessingRef.current = true;
    setTokenProcessed(true);
    retryCountRef.current = retryCount;
    
    try {
      const response = await restaurantService.createTableRequestFromQR(appName, token);
      
      if (response.requiresLogin) {
        // Only show login modal if user is not authenticated in Redux
        // If user is authenticated but API still says requiresLogin, it might be a cookie issue
        if (!isAuthenticated || !user) {
          setLoginModalTitle('Login Required');
          setLoginModalMessage('Sign in to request a table.');
          loginSuccessCallbackRef.current = null;
          setShowLoginModal(true);
          isProcessingRef.current = false;
          setTokenProcessed(false); // Reset so we can retry after login
        } else if (retryCount < 2) {
          // User is authenticated but API still requires login - might be cookie not set yet
          // Retry after a delay (increasing delay for each retry)
          isProcessingRef.current = false;
          setTokenProcessed(false);
          const delay = (retryCount + 1) * 1000; // 1s, 2s
          console.log(`Retrying table request (attempt ${totalAttempts}/3) after ${delay}ms...`);
          // Clear any existing timer
          if (tokenCallTimerRef.current) {
            clearTimeout(tokenCallTimerRef.current);
          }
          tokenCallTimerRef.current = setTimeout(() => {
            if (!isProcessingRef.current) {
              handleQRToken(token, retryCount + 1);
            }
            tokenCallTimerRef.current = null;
          }, delay);
        } else {
          // Max retries reached, show toast message
          setToastMessage('Unable to process table request. Please contact the restaurant for assistance.');
          isProcessingRef.current = false;
          setTokenProcessed(true);
        }
      } else if (response.success) {
        // Check if should redirect to join order page
        if (response.redirectToJoin && response.orderId && response.tableId) {
          // Redirect to join order page
          navigate(`/restaurants/${appName}/join-order?orderId=${response.orderId}&tableId=${response.tableId}`, { replace: true });
          isProcessingRef.current = false;
          setTokenProcessed(true);
          return;
        }
        
        // Check if should redirect to menu (user already joined)
        if (response.redirectToMenu && response.orderId) {
          // Redirect to menu with order ID
          const newSearchParams = new URLSearchParams(searchParams);
          newSearchParams.delete('token');
          newSearchParams.set('type', '1'); // Dine-in
          newSearchParams.set('order', response.orderId.toString());
          navigate(`/restaurants/${appName}/menu?${newSearchParams.toString()}`, { replace: true });
          isProcessingRef.current = false;
          setTokenProcessed(true);
          return;
        }
        
        // Check if table already exists
        if (response.tableAlreadyExists) {
          // Show toast with table number
          const tableMsg = response.tableNumber
            ? `${response.displayLabel || formatSpaceLabel(response.spaceType, response.tableNumber)} already exists`
            : 'Table already exists';
          setToastMessage(tableMsg);
          isProcessingRef.current = false;
          setTokenProcessed(true);
        } else {
          // Table request created or already assigned
          setTableRequestStatus({
            requestId: response.requestId,
            allotted: response.allotted,
            tableNumber: response.tableNumber,
            spaceType: response.spaceType,
            displayLabel: response.displayLabel,
            message: response.message || (response.allotted ? 'Table already assigned' : 'Table request created. Waiting for approval...')
          });
          
          // Remove token from URL; keep dine-in so checkout does not restore takeaway/delivery
          const newSearchParams = new URLSearchParams(searchParams);
          newSearchParams.delete('token');
          newSearchParams.set('type', '1');
          navigate(`/restaurants/${appName}/menu?${newSearchParams.toString()}`, { replace: true });
          isProcessingRef.current = false;
        }
      } else {
        // Non-retryable error
        setToastMessage(response.message || 'Error creating table request. Please contact the restaurant for assistance.');
        isProcessingRef.current = false;
        setTokenProcessed(true);
      }
    } catch (err: any) {
      console.error('Error handling QR token:', err);
      // Check if it's a 401 or if requiresLogin is in the response
      if (err.response?.status === 401 || err.response?.data?.requiresLogin) {
        // Only show login modal if user is not authenticated
        if (!isAuthenticated || !user) {
          setLoginModalTitle('Login Required');
          setLoginModalMessage('Sign in to request a table.');
          loginSuccessCallbackRef.current = null;
          setShowLoginModal(true);
          isProcessingRef.current = false;
          setTokenProcessed(false); // Reset so we can retry after login
        } else if (retryCount < 2) {
          // User is authenticated but API still returns 401 - might be cookie not set yet
          // Retry after a delay (increasing delay for each retry)
          isProcessingRef.current = false;
          setTokenProcessed(false);
          const delay = (retryCount + 1) * 1000; // 1s, 2s
          console.log(`Retrying table request (attempt ${totalAttempts}/3) after ${delay}ms...`);
          // Clear any existing timer
          if (tokenCallTimerRef.current) {
            clearTimeout(tokenCallTimerRef.current);
          }
          tokenCallTimerRef.current = setTimeout(() => {
            if (!isProcessingRef.current) {
              handleQRToken(token, retryCount + 1);
            }
            tokenCallTimerRef.current = null;
          }, delay);
        } else {
          // Max retries reached, show toast message
          setToastMessage('Unable to process table request. Please contact the restaurant for assistance.');
          isProcessingRef.current = false;
          setTokenProcessed(true);
        }
      } else {
        // Non-retryable error or max retries reached
        if (retryCount < 2) {
          // Retry for other errors too
          isProcessingRef.current = false;
          setTokenProcessed(false);
          const delay = (retryCount + 1) * 1000;
          console.log(`Retrying table request after error (attempt ${totalAttempts}/3) after ${delay}ms...`);
          setTimeout(() => {
            handleQRToken(token, retryCount + 1);
          }, delay);
        } else {
          setToastMessage(err.response?.data?.message || 'Error processing QR code. Please contact the restaurant for assistance.');
          isProcessingRef.current = false;
          setTokenProcessed(true);
        }
      }
    }
  };

  const handleLoginSuccess = () => {
    setShowLoginModal(false);
    const wishlistRetry = loginSuccessCallbackRef.current;
    if (wishlistRetry) {
      loginSuccessCallbackRef.current = null;
      setTimeout(() => wishlistRetry(), 400);
      return;
    }
    // Reset token processed flag and refs so we can retry after login
    isProcessingRef.current = false;
    retryCountRef.current = 0;
    setTokenProcessed(false);
    // The useEffect that watches isAuthenticated will automatically retry when Redux store updates
  };

  const promptWishlistLogin = (retry: () => void) => {
    loginSuccessCallbackRef.current = retry;
    setLoginModalTitle('Sign in to continue');
    setLoginModalMessage('Sign in to save dishes and view your wishlist.');
    setShowLoginModal(true);
  };

  const promptEmailSignIn = useCallback(() => {
    loginSuccessCallbackRef.current = null;
    setLoginModalTitle('Sign in to continue');
    setLoginModalMessage('Sign in to view the menu and place your order.');
    setShowLoginModal(true);
  }, []);

  useEmailSignInPrompt({ onPrompt: promptEmailSignIn });

  const loadMenu = async (requestId: number) => {
    if (!appName) return;

    menuHydratedRef.current = false;
    const showSkeleton = !menuDataReadyRef.current;
    if (showSkeleton) {
      setLoading(true);
    }
    setError(null);

    try {
      const response = await restaurantService.getRestaurantMenu(appName, sortBy);
      if (requestId !== loadRequestRef.current) return;

      if (response.success) {
        setRestaurant(response.restaurant);
        setDisplaySettings(response.displaySettings ?? null);
        const items = response.menu.menuList || [];
        setMenuItems(items);
        setAllMenuItems(items);
        setCategories(response.categories || []);
        setCuisines(response.cuisines || []);

        const allTags = new Set<string>();
        (response.menu.menuList || []).forEach((item: MenuItem) => {
          if (item.tags) {
            item.tags.forEach((tag) => {
              if (tag && tag.trim() !== '') {
                allTags.add(tag);
              }
            });
          }
        });
        setTags(Array.from(allTags));
        const maxItemPrice = items.length > 0 ? Math.max(...items.map((item) => item.price || 0)) : 0;
        const sliderMax = Math.max(500, Math.ceil(maxItemPrice / 50) * 50);
        setPriceRange([0, sliderMax]);
      } else {
        setError(response.message || 'Failed to load menu');
      }
    } catch (err: any) {
      if (requestId === loadRequestRef.current) {
        setError(err.response?.data?.message || 'Error loading menu');
      }
    } finally {
      if (requestId === loadRequestRef.current) {
        setLoading(false);
        menuDataReadyRef.current = true;
        menuHydratedRef.current = true;
      }
    }

    if (requestId !== loadRequestRef.current) return;

    try {
      const [promoRes, couponRes] = await Promise.all([
        cartService.getActivePromotions(appName),
        restaurantService.getPublicCoupons(appName),
      ]);
      if (promoRes.success) {
        setActivePromotions(promoRes.promotions || []);
      }
      if (couponRes.success) {
        setPublicCoupons(couponRes.coupons || []);
      }
    } catch (_) { /* optional */ }
  };

  const loadCartCount = async () => {
    if (!appName) return;
    try {
      const response = await cartService.getCartCount(appName);
      if (response.success) {
        setCartCount(response.cartCount || 0);
      }
    } catch {
      // Cart count is optional on the menu page
    }
  };

  const priceSliderMax = useMemo(() => {
    if (allMenuItems.length === 0) return 1000;
    const max = Math.max(...allMenuItems.map((item) => item.price || 0));
    return Math.max(500, Math.ceil(max / 50) * 50);
  }, [allMenuItems]);

  allMenuItemsRef.current = allMenuItems;

  const hasActiveMenuFilters = useCallback(
    (sliderMax: number) =>
      itemType !== 'all' ||
      selectedCategories.length > 0 ||
      selectedCuisines.length > 0 ||
      selectedTags.length > 0 ||
      showWishlist ||
      sortBy !== 'default' ||
      priceRange[1] < sliderMax,
    [itemType, selectedCategories, selectedCuisines, selectedTags, showWishlist, sortBy, priceRange]
  );

  const applyFilter = useCallback(async () => {
    if (!appName) return;

    try {
      const priceFilterActive = priceRange[1] < priceSliderMax;
      const filter: MenuFilter = {
        itemType,
        categories: selectedCategories,
        cuisines: selectedCuisines,
        tags: selectedTags,
        priceFilterEnabled: priceFilterActive,
        minPrice: 0,
        maxPrice: priceRange[1],
      };

      const response = await restaurantService.filterMenu(appName, {
        ...filter,
        sortBy: sortBy,
      });
      if (response.success) {
        setMenuItems(response.menuItems || []);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error applying filter');
    }
  }, [appName, itemType, selectedCategories, selectedCuisines, selectedTags, priceRange, priceSliderMax, sortBy]);

  const fetchWishlistMenu = async () => {
    if (!appName) return;
    if (!isAuthenticated) {
      promptWishlistLogin(() => {
        void fetchWishlistMenu();
      });
      return;
    }
    try {
      const response = await restaurantService.getWishlistMenu(appName);
      if (response.success) {
        setMenuItems(response.menuItems || []);
        setShowWishlist(true);
        return;
      }
      if (isWishlistLoginRequired(undefined, response.message, (response as { requiresLogin?: boolean }).requiresLogin)) {
        promptWishlistLogin(() => {
          void fetchWishlistMenu();
        });
        return;
      }
      setMessageDialog({
        title: 'Wishlist',
        message: response.message || 'Could not load your wishlist.',
        variant: 'error',
      });
    } catch (err: any) {
      const data = err.response?.data;
      if (isWishlistLoginRequired(err.response?.status, data?.message, data?.requiresLogin)) {
        promptWishlistLogin(() => {
          void fetchWishlistMenu();
        });
        return;
      }
      setMessageDialog({
        title: 'Wishlist',
        message: data?.message || 'Could not load your wishlist.',
        variant: 'error',
      });
    }
  };

  const handleAddToCart = async (
    menuId: number,
    quantity: number,
    options?: CartOptions,
    itemQuantity?: number,
    flags?: MenuCardAddToCartFlags
  ): Promise<CartActionResult> => {
    // Shown inside the open options sheet instead (one error message, not two).
    const showError = (message: string) => {
      if (!flags?.quietErrors) setToastMessage(message);
    };
    if (!appName) return { success: false };
    if (!isAcceptingGuestOrders(guestOrdering)) {
      const message = guestOrderingMessage(guestOrdering) || 'This restaurant is not accepting customer orders right now.';
      showError(message);
      return { success: false, message };
    }
    try {
      const response = await cartService.addToCart(appName, menuId, quantity, guestOrderType, options);
      if (response.success) {
        setCartCount(response.cartCount || 0);
        setMenuItems(prevItems =>
          prevItems.map(item =>
            item.id === menuId
              ? { ...item, qty: itemQuantity ?? quantity }
              : item
          )
        );
        return { success: true };
      }
      const message = response.message || 'Could not update your cart';
      showError(message);
      if (appName && message.includes('payment for this order is in progress')) {
        void promptCancelOpenPayment(appName)
          .then((cancelled) => {
            if (cancelled) setToastMessage('Payment cancelled. You can add the item again.');
          })
          .catch(() => setToastMessage('Could not cancel the payment. Open the order and try again.'));
      }
      return { success: false, message };
    } catch (err: unknown) {
      const message = apiErrorMessage(err, 'Error adding item to cart');
      showError(message);
      if (appName && message.includes('payment for this order is in progress')) {
        void promptCancelOpenPayment(appName)
          .then((cancelled) => {
            if (cancelled) setToastMessage('Payment cancelled. You can add the item again.');
          })
          .catch(() => setToastMessage('Could not cancel the payment. Open the order and try again.'));
      }
      return { success: false, message };
    }
  };

  const handleToggleWishlist = async (menuId: number, currentState: boolean, wishlistId?: number) => {
    if (!appName) return;
    if (!isAuthenticated) {
      promptWishlistLogin(() => {
        void handleToggleWishlist(menuId, currentState, wishlistId);
      });
      return;
    }
    try {
      const response = await wishlistService.toggleWishlist(appName, menuId, !currentState, wishlistId);
      if (response.success) {
        setMenuItems(prevItems =>
          prevItems.map(item =>
            item.id === menuId
              ? { ...item, inWishlist: response.active, wishlistId: wishlistId }
              : item
          )
        );
        return;
      }
      if (isWishlistLoginRequired(undefined, response.message)) {
        promptWishlistLogin(() => {
          void handleToggleWishlist(menuId, currentState, wishlistId);
        });
        return;
      }
      setMessageDialog({
        title: 'Wishlist',
        message: response.message || 'Could not update your wishlist.',
        variant: 'error',
      });
    } catch (err: any) {
      const data = err.response?.data;
      if (isWishlistLoginRequired(err.response?.status, data?.message, data?.requiresLogin)) {
        promptWishlistLogin(() => {
          void handleToggleWishlist(menuId, currentState, wishlistId);
        });
        return;
      }
      setMessageDialog({
        title: 'Wishlist',
        message: data?.message || 'Could not update your wishlist.',
        variant: 'error',
      });
    }
  };

  const handleCategoryToggle = (categoryId: number) => {
    setShowWishlist(false);
    setSelectedCategories((prev) =>
      prev.includes(categoryId)
        ? prev.filter((id) => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const handleCategoryBadgeClick = (categoryId: number | null) => {
    setShowWishlist(false);
    if (categoryId === null) {
      setSelectedCategories([]);
      return;
    }
    setSelectedCategories(prev =>
      prev.length === 1 && prev[0] === categoryId ? [] : [categoryId]
    );
  };

  const categoryCounts = useMemo(() => {
    const counts = new Map<number, number>();
    allMenuItems.forEach((item) => {
      if (item.categoryId) {
        counts.set(item.categoryId, (counts.get(item.categoryId) || 0) + 1);
      }
    });
    return counts;
  }, [allMenuItems]);

  const activeCategoryBadge =
    selectedCategories.length === 1 ? selectedCategories[0] : null;

  const groupedMenuSections = useMemo(() => {
    if (activeCategoryBadge !== null) {
      return [{ label: null as string | null, items: menuItems }];
    }
    const byCategory = new Map<number, MenuItem[]>();
    const uncategorized: MenuItem[] = [];
    menuItems.forEach((item) => {
      if (item.categoryId) {
        const list = byCategory.get(item.categoryId) || [];
        list.push(item);
        byCategory.set(item.categoryId, list);
      } else {
        uncategorized.push(item);
      }
    });
    const sections: { label: string | null; items: MenuItem[] }[] = [];
    categories.forEach((cat) => {
      const items = byCategory.get(cat.id);
      if (items?.length) {
        sections.push({ label: cat.type, items });
      }
    });
    if (uncategorized.length) {
      sections.push({ label: 'Other', items: uncategorized });
    }
    return sections.length ? sections : [{ label: null, items: menuItems }];
  }, [menuItems, activeCategoryBadge, categories]);

  useEffect(() => {
    const el = toolbarRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setShowScrollFilterFab(!entry.isIntersecting),
      { threshold: 0, rootMargin: '-72px 0px 0px 0px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [loading, restaurant]);

  const handleCuisineToggle = (cuisineId: number) => {
    setSelectedCuisines(prev =>
      prev.includes(cuisineId)
        ? prev.filter(id => id !== cuisineId)
        : [...prev, cuisineId]
    );
  };

  const handleTagToggle = (tag: string) => {
    setSelectedTags(prev =>
      prev.includes(tag)
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  const handleItemTypeToggle = (type: 'veg' | 'nonveg') => {
    if (itemType === type) {
      setItemType('all');
    } else if (itemType === 'all') {
      setItemType(type);
    } else {
      setItemType('all');
    }
  };

  const handleSortChange = (sortOption: string) => {
    setSortBy(sortOption);
    // Trigger filter to apply sorting on backend
    applyFilter();
  };

  const clearFilter = () => {
    setSelectedCategories([]);
    setSelectedCuisines([]);
    setSelectedTags([]);
    setItemType('all');
    setPriceRange([0, priceSliderMax]);
    setSortBy('default');
    setShowWishlist(false);
    loadMenu(++loadRequestRef.current);
  };

  const handleWishlistViewChange = async (enabled: boolean) => {
    if (enabled) {
      await fetchWishlistMenu();
      return;
    }
    setShowWishlist(false);
    loadMenu(++loadRequestRef.current);
  };

  const handleMobileFilterApply = () => {
    if (showWishlist) {
      fetchWishlistMenu();
      return;
    }
    applyFilter();
  };

  useEffect(() => {
    if (!menuHydratedRef.current || !appName || loading) {
      return undefined;
    }

    const timer = setTimeout(() => {
      if (showWishlist) {
        fetchWishlistMenu();
        return;
      }
      if (!hasActiveMenuFilters(priceSliderMax)) {
        setMenuItems(allMenuItemsRef.current);
        return;
      }
      applyFilter();
    }, 300);

    return () => clearTimeout(timer);
  }, [
    appName,
    loading,
    itemType,
    selectedCategories,
    selectedCuisines,
    selectedTags,
    priceRange,
    priceSliderMax,
    showWishlist,
    hasActiveMenuFilters,
    applyFilter,
  ]);

  const formatPrice = (price: number): string => {
    const rounded = roundDisplayPrice(price);
    if (!restaurant || !restaurant.currencySymbol) return String(rounded);
    let currencySymbol = restaurant.currencySymbol;
    const textarea = document.createElement('textarea');
    textarea.innerHTML = currencySymbol;
    currencySymbol = textarea.value;
    return `${currencySymbol}${rounded}`;
  };

  const restaurantBreadcrumbLabel = getRestaurantBreadcrumbLabel(
    restaurant?.restaurantName,
    appName
  );

  const offersCount = activePromotions.length + publicCoupons.length;

  const openOffersDrawer = useCallback(() => {
    if (offersCount > 0) {
      setOffersDrawerOpen(true);
    }
  }, [offersCount]);

  const couponCurrencySymbol = useMemo(() => {
    if (!restaurant?.currencySymbol) return '';
    if (typeof document === 'undefined') {
      return restaurant.currencySymbol;
    }
    const textarea = document.createElement('textarea');
    textarea.innerHTML = restaurant.currencySymbol;
    return textarea.value;
  }, [restaurant?.currencySymbol]);

  return (
    <div className="restaurant-menu-page">
      <SeoHead
        title={restaurant ? buildMenuPageTitle(restaurant) : 'Menu'}
        description={restaurant ? buildMenuPageDescription(restaurant) : 'Restaurant menu'}
        path={appName ? `/restaurants/${appName}/menu` : '/restaurants'}
        noIndex={!shouldIndexMenuPage(restaurant, menuItems)}
        images={
          restaurant?.coverImage
            ? [getRestaurantImageUrl(restaurant.id, restaurant.coverImage)]
            : restaurant?.appLogo
              ? [getRestaurantImageUrl(restaurant.id, restaurant.appLogo)]
              : undefined
        }
      />
      <Header />
      <PageContainer>
      <div className="restaurant-menu-content">
        <div className="menu-page-shell">
          <Breadcrumb
            items={[
              { label: restaurantBreadcrumbLabel, path: restaurantHomePath(appName) },
              { label: 'Menu', active: true },
            ]}
          />
          <GuestOrderingBanner status={guestOrdering} compact />

          {error && (
            <div className="error-section">
              <p>{error}</p>
            </div>
          )}
        </div>

        {loading && !error ? (
          <MenuPageSkeleton />
        ) : !loading && !error && restaurant ? (
          <div className="menu-layout-fullbleed">
            <div className="menu-layout">
              <aside className="sidebar-left menu-filter-sidebar">
                <DesktopFilterSidebar
                  categories={categories}
                  cuisines={cuisines}
                  tags={tags}
                  selectedCategories={selectedCategories}
                  selectedCuisines={selectedCuisines}
                  selectedTags={selectedTags}
                  priceRange={priceRange}
                  priceSliderMax={priceSliderMax}
                  formatPrice={formatPrice}
                  onCategoryToggle={handleCategoryToggle}
                  onCuisineToggle={handleCuisineToggle}
                  onTagToggle={handleTagToggle}
                  onPriceRangeChange={setPriceRange}
                  onClear={clearFilter}
                />
              </aside>

              <div className="menu-main-content">
                <div className="menu-toolbar" ref={toolbarRef}>
                  <div className="filter-pills">
                    <button
                      type="button"
                      className={`diet-pill ${itemType === 'veg' ? 'active' : ''}`}
                      onClick={() => handleItemTypeToggle('veg')}
                    >
                      <span className="diet-dot" />
                      Veg
                    </button>
                    <button
                      type="button"
                      className={`diet-pill non-veg ${itemType === 'nonveg' ? 'active' : ''}`}
                      onClick={() => handleItemTypeToggle('nonveg')}
                    >
                      <span className="diet-dot" />
                      Non Veg
                    </button>
                  </div>

                  <div className="toolbar-right">
                    {appName && (
                      <MenuViewSwitch
                        appName={appName}
                        active="classic"
                        cuisineId={selectedCuisines[0] ?? null}
                        categoryId={selectedCategories[0] ?? null}
                      />
                    )}
                    <button
                      type="button"
                      className={`bookmark-btn ${showWishlist ? 'saved' : ''}`}
                      onClick={() => handleWishlistViewChange(!showWishlist)}
                      aria-label={showWishlist ? 'Show all menu items' : 'Show saved items'}
                      aria-pressed={showWishlist}
                      title="Saved items"
                    >
                      {showWishlist ? <IconBookmarkFilled /> : <IconBookmark />}
                    </button>
                    <button
                      type="button"
                      className="filter-mobile-btn filter-mobile-btn--toolbar"
                      onClick={() => {
                        window.dispatchEvent(new CustomEvent('maznut:close-menu-options-sheet'));
                        setMobileFilterOpen(true);
                      }}
                      aria-label="Open filters"
                    >
                      <IconFilterSliders />
                    </button>
                    {canOrder && (
                    <Link to={withGuestOrderTypeQuery(`/restaurants/${appName}/cart`, appName, guestOrderType)} className="checkout-btn">
                      <IconCart />
                      Checkout
                      {cartCount > 0 && <span className="checkout-count">{cartCount}</span>}
                    </Link>
                    )}
                  </div>
                </div>

                {categories.length > 0 && (
                  <div
                    className="category-badge-strip category-badge-strip--compact"
                    role="tablist"
                    aria-label="Menu categories"
                  >
                    <button
                      type="button"
                      role="tab"
                      aria-selected={activeCategoryBadge === null}
                      className={`category-badge ${activeCategoryBadge === null ? 'active' : ''}`}
                      onClick={() => handleCategoryBadgeClick(null)}
                    >
                      All
                      <span className="category-badge-count">{allMenuItems.length}</span>
                    </button>
                    {categories
                      .filter((cat) => cat?.id && cat.type?.trim())
                      .map((cat) => (
                        <button
                          key={cat.id}
                          type="button"
                          role="tab"
                          aria-selected={activeCategoryBadge === cat.id}
                          className={`category-badge ${activeCategoryBadge === cat.id ? 'active' : ''}`}
                          onClick={() => handleCategoryBadgeClick(cat.id)}
                        >
                          {cat.type}
                          <span className="category-badge-count">
                            {categoryCounts.get(cat.id) || 0}
                          </span>
                        </button>
                      ))}
                  </div>
                )}

                <div className={`menu-items-grid${displaySettings?.showMenuItemImages === true ? '' : ' menu-items-grid--no-image'}`}>
                  {groupedMenuSections.map((section) => (
                    <React.Fragment key={section.label || 'all'}>
                      {section.label && (
                        <div className="section-label">{section.label}</div>
                      )}
                      {section.items.map((item) => (
                        <MenuItemCard
                          key={item.id}
                          item={item}
                          restaurant={restaurant}
                          onAddToCart={handleAddToCart}
                          onToggleWishlist={handleToggleWishlist}
                          formatPrice={formatPrice}
                          orderingEnabled={canOrderLoaded ? canOrder : undefined}
                          showImages={displaySettings?.showMenuItemImages === true}
                        />
                      ))}
                    </React.Fragment>
                  ))}
                </div>

                {menuItems.length === 0 && (
                  <div className="no-menu-items">
                    <img
                      src={DEFAULT_IMAGES.NO_ITEMS_FOUND}
                      alt="No items found"
                      className="no-menu-items-image"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                    <h3>Oops! No Items Found</h3>
                    <p>
                      We couldn't find any menu items matching your filters.
                      Try adjusting your search criteria or clear the filters to see all available items.
                    </p>
                  </div>
                )}
              </div>

              {canOrder && (
              <div className="mobile-fab-checkout">
                <Link to={withGuestOrderTypeQuery(`/restaurants/${appName}/cart`, appName, guestOrderType)} className="fab fab-cart" aria-label="Go to cart">
                  <IconCartFab />
                  {cartCount > 0 && <span className="fab-badge">{cartCount}</span>}
                </Link>
              </div>
              )}
              <div className={`mobile-fab-filter ${showScrollFilterFab ? 'is-visible' : ''}`}>
                <button
                  type="button"
                  className="fab fab-filter"
                  onClick={() => {
                    window.dispatchEvent(new CustomEvent('maznut:close-menu-options-sheet'));
                    setMobileFilterOpen(true);
                  }}
                  aria-label="Open filters"
                >
                  <IconFilterSlidersFab />
                </button>
              </div>
            </div>
          </div>
        ) : !loading && !error ? (
          <div className="error-section">
            <p>This menu could not be loaded. Refresh and try again.</p>
          </div>
        ) : null}
      </div>

      {/* Mobile Filter Overlay */}
      {mobileFilterOpen && (
        <MobileMenuFilterOverlay
          categories={categories}
          selectedCategories={selectedCategories}
          cuisines={cuisines}
          tags={tags}
          selectedCuisines={selectedCuisines}
          selectedTags={selectedTags}
          itemType={itemType}
          priceRange={priceRange}
          priceSliderMax={priceSliderMax}
          formatPrice={formatPrice}
          onCategoryToggle={handleCategoryToggle}
          onCuisineToggle={handleCuisineToggle}
          onTagToggle={handleTagToggle}
          onItemTypeToggle={handleItemTypeToggle}
          onPriceRangeChange={setPriceRange}
          sortBy={sortBy}
          onSortChange={handleSortChange}
          showWishlist={showWishlist}
          onWishlistChange={setShowWishlist}
          onClear={clearFilter}
          onClose={() => setMobileFilterOpen(false)}
          onApply={handleMobileFilterApply}
        />
      )}

      </PageContainer>
      <Footer variant="restaurant" restaurant={restaurant} />

      {/* Login Modal */}
      <LoginModal
        isOpen={showLoginModal}
        onClose={() => {
          loginSuccessCallbackRef.current = null;
          setShowLoginModal(false);
        }}
        title={loginModalTitle}
        message={loginModalMessage}
        onLoginSuccess={handleLoginSuccess}
      />

      <MessageDialog
        isOpen={messageDialog != null}
        onClose={() => setMessageDialog(null)}
        title={messageDialog?.title}
        message={messageDialog?.message || ''}
        variant={messageDialog?.variant}
      />

      {/* Table Request Status Banner */}
      {tableRequestStatus && (
        <div className={`table-request-banner ${tableRequestStatus.allotted ? 'approved' : 'pending'}`}>
          <div className="banner-content">
            <i className={`fa ${tableRequestStatus.allotted ? 'fa-check-circle' : 'fa-clock-o'}`}></i>
            <span>{tableRequestStatus.message}</span>
            {tableRequestStatus.tableNumber && (
              <span className="table-number">{tableRequestStatus.displayLabel || formatSpaceLabel(tableRequestStatus.spaceType, tableRequestStatus.tableNumber)}</span>
            )}
            <button className="close-banner" onClick={() => setTableRequestStatus(null)}>
              <i className="fa fa-times"></i>
            </button>
          </div>
        </div>
      )}
      
      {/* Toast Notification */}
      {toastMessage && (
        <Toast
          message={toastMessage}
          type="error"
          onClose={() => setToastMessage(null)}
        />
      )}

      <OffersDrawer
        open={offersDrawerOpen}
        onClose={() => setOffersDrawerOpen(false)}
        promotions={activePromotions}
        coupons={publicCoupons}
        formatCouponBadge={(coupon) => formatCouponBadge(coupon, couponCurrencySymbol)}
        formatCouponText={(coupon) => formatCouponText(coupon, formatPrice)}
      />
    </div>
  );
};

// Desktop sticky left sidebar — category, cuisine, tags, price
interface DesktopFilterSidebarProps {
  categories: Category[];
  cuisines: Cuisine[];
  tags: string[];
  selectedCategories: number[];
  selectedCuisines: number[];
  selectedTags: string[];
  priceRange: [number, number];
  priceSliderMax: number;
  formatPrice: (price: number) => string;
  onCategoryToggle: (id: number) => void;
  onCuisineToggle: (id: number) => void;
  onTagToggle: (tag: string) => void;
  onPriceRangeChange: (range: [number, number]) => void;
  onClear: () => void;
}

const DesktopFilterSidebar: React.FC<DesktopFilterSidebarProps> = ({
  categories,
  cuisines,
  tags,
  selectedCategories,
  selectedCuisines,
  selectedTags,
  priceRange,
  priceSliderMax,
  formatPrice,
  onCategoryToggle,
  onCuisineToggle,
  onTagToggle,
  onPriceRangeChange,
  onClear,
}) => (
  <>
    {categories.length > 0 && (
      <div className="filter-section">
        <div className="filter-title">Category</div>
        {categories
          .filter((c) => c?.id && c.type?.trim())
          .map((cat) => (
            <label
              key={cat.id}
              className={`checkbox-item ${selectedCategories.includes(cat.id) ? 'active' : ''}`}
            >
              <input
                type="checkbox"
                checked={selectedCategories.includes(cat.id)}
                onChange={() => onCategoryToggle(cat.id)}
              />
              <span className="custom-check" />
              {cat.type}
            </label>
          ))}
      </div>
    )}

    {cuisines.length > 0 && (
      <div className="filter-section">
        <div className="filter-title">Cuisine</div>
        {cuisines
          .filter((c) => c?.id && c.name?.trim())
          .map((cuisine) => (
            <label
              key={cuisine.id}
              className={`checkbox-item ${selectedCuisines.includes(cuisine.id) ? 'active' : ''}`}
            >
              <input
                type="checkbox"
                checked={selectedCuisines.includes(cuisine.id)}
                onChange={() => onCuisineToggle(cuisine.id)}
              />
              <span className="custom-check" />
              {cuisine.name}
            </label>
          ))}
      </div>
    )}

    {tags.length > 0 && (
      <div className="filter-section">
        <div className="filter-title">Tags</div>
        <div className="tag-list">
          {tags
            .filter((tag) => tag?.trim())
            .map((tag) => (
              <button
                key={tag}
                type="button"
                className={`tag-pill ${selectedTags.includes(tag) ? 'active' : ''}`}
                onClick={() => onTagToggle(tag)}
              >
                {tag}
              </button>
            ))}
        </div>
      </div>
    )}

    <div className="filter-section filter-section--price">
      <div className="filter-title">Price Range</div>
      <div className="price-range-slider">
        <input
          type="range"
          min={0}
          max={priceSliderMax}
          value={priceRange[1]}
          onChange={(e) => onPriceRangeChange([0, Number(e.target.value)])}
          className="price-slider"
          aria-label="Maximum price"
        />
        <div className="price-range-label">
          Up to {formatPrice(priceRange[1])}
        </div>
      </div>
    </div>

    <button type="button" className="sidebar-clear-btn" onClick={onClear}>
      Clear all filters
    </button>
  </>
);

export default RestaurantMenu;
